import { memo, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { AlertTriangle, MapPinOff, Plus, RefreshCw } from 'lucide-react'

import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

import type { CandidatesStatus } from '../hooks/use-nearby-candidates'
import { formatDistance } from '../utils/distance'
import type { NearbyCandidate } from '../utils/nearby-workers'
import { WorkerAvatar } from './status-meta'

const FADE_MS = 300
const FOCUS_HIGHLIGHT_MS = 1_500

interface DisplayedCandidate extends NearbyCandidate {
  leaving: boolean
}

/**
 * Candidates that disappear (went offline/busy, or were just added to the job) stay for a
 * short fade instead of vanishing mid-scroll.
 */
function useFadingCandidates(candidates: NearbyCandidate[]): DisplayedCandidate[] {
  const [leaving, setLeaving] = useState<Map<string, NearbyCandidate>>(() => new Map())
  const previousRef = useRef(candidates)
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  useEffect(() => {
    const currentIds = new Set(candidates.map((candidate) => candidate.worker.id))
    const gone = previousRef.current.filter((candidate) => !currentIds.has(candidate.worker.id))
    previousRef.current = candidates
    setLeaving((current) => {
      const back = Array.from(current.keys()).filter((id) => currentIds.has(id))
      if (gone.length === 0 && back.length === 0) return current
      const next = new Map(current)
      back.forEach((id) => next.delete(id))
      gone.forEach((candidate) => next.set(candidate.worker.id, candidate))
      return next
    })
    gone.forEach((candidate) => {
      const id = candidate.worker.id
      clearTimeout(timersRef.current.get(id))
      timersRef.current.set(id, setTimeout(() => {
        timersRef.current.delete(id)
        setLeaving((current) => {
          if (!current.has(id)) return current
          const next = new Map(current)
          next.delete(id)
          return next
        })
      }, FADE_MS))
    })
  }, [candidates])

  useEffect(() => () => timersRef.current.forEach((timer) => clearTimeout(timer)), [])

  return useMemo(() => {
    if (leaving.size === 0) return candidates.map((candidate) => ({ ...candidate, leaving: false }))
    return [
      ...candidates.map((candidate) => ({ ...candidate, leaving: false })),
      ...Array.from(leaving.values()).map((candidate) => ({ ...candidate, leaving: true })),
    ].sort((a, b) => a.distanceKm - b.distanceKm)
  }, [candidates, leaving])
}

const CandidateChip = memo(function CandidateChip({
  candidate,
  isHovered,
  isHighlighted,
  onAdd,
  onHover,
}: {
  candidate: DisplayedCandidate
  isHovered: boolean
  isHighlighted: boolean
  onAdd: (candidate: NearbyCandidate) => void
  onHover: (workerId: string | null) => void
}) {
  const { worker, distanceKm, etaMinutes, hasConflict, leaving } = candidate
  return (
    <li
      data-candidate-chip={worker.id}
      aria-hidden={leaving || undefined}
      onMouseEnter={() => onHover(worker.id)}
      onMouseLeave={() => onHover(null)}
      className={cn(
        'flex h-9 w-[224px] shrink-0 snap-start items-center gap-1.5 rounded-lg border bg-white pl-1.5 pr-1 transition-[opacity,box-shadow,border-color] duration-300',
        isHighlighted ? 'border-blue-500 ring-2 ring-blue-400' : isHovered ? 'border-blue-300' : 'border-[#dfeae3]',
        leaving && 'pointer-events-none opacity-0',
      )}
    >
      <WorkerAvatar name={worker.name} avatarUrl={worker.avatarUrl} className="h-6 w-6" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[11px] font-semibold leading-tight text-[#20352b]">{worker.name}</p>
        <p className="flex items-center gap-1 text-[10px] leading-tight text-[#718078]">
          <span className="shrink-0 tabular-nums">{formatDistance(distanceKm)} · {etaMinutes} min</span>
          {hasConflict ? (
            <span className="inline-flex min-w-0 items-center gap-0.5 truncate rounded bg-amber-100 px-0.5 text-[9px] font-semibold text-amber-800">
              <AlertTriangle className="h-2.5 w-2.5" aria-hidden="true" />
              Conflict
            </span>
          ) : null}
        </p>
      </div>
      <button
        type="button"
        disabled={leaving}
        onClick={() => onAdd(candidate)}
        onFocus={() => onHover(worker.id)}
        onBlur={() => onHover(null)}
        aria-label={`Add ${worker.name}, ${formatDistance(distanceKm)} away, about ${etaMinutes} minutes${hasConflict ? ', has a schedule conflict' : ''}`}
        className="inline-flex h-6 shrink-0 items-center gap-0.5 rounded-md bg-[#169b62] px-1.5 text-[11px] font-semibold text-white hover:bg-[#118753] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#169b62] focus-visible:ring-offset-1"
      >
        <Plus className="h-3 w-3" aria-hidden="true" />
        Add
      </button>
    </li>
  )
})

export interface NearbyWorkersStripProps {
  jobId: string
  categoryName: string | null
  status: CandidatesStatus
  /** Candidates to show (already limited by "Show more"). */
  candidates: NearbyCandidate[]
  totalCount: number
  jobLocated: boolean
  radiusKm: number
  canIncreaseRadius: boolean
  hoveredWorkerId: string | null
  focusedCandidate: { workerId: string; token: number } | null
  /** overlay: floats over the bottom of the map. inline: inside the mobile bottom sheet. */
  variant: 'overlay' | 'inline'
  onAdd: (candidate: NearbyCandidate) => void
  onHover: (workerId: string | null) => void
  onRetry: () => void
  onIncreaseRadius: () => void
  onShowMore: () => void
}

/**
 * "Nearby {category} workers" for the selected job: one horizontally scrolling row of chips
 * (nearest first) with Add, kept to ~64px so the map stays visible.
 */
export const NearbyWorkersStrip = memo(function NearbyWorkersStrip({
  jobId,
  categoryName,
  status,
  candidates,
  totalCount,
  jobLocated,
  radiusKm,
  canIncreaseRadius,
  hoveredWorkerId,
  focusedCandidate,
  variant,
  onAdd,
  onHover,
  onRetry,
  onIncreaseRadius,
  onShowMore,
}: NearbyWorkersStripProps) {
  const displayed = useFadingCandidates(candidates)
  const scrollerRef = useRef<HTMLUListElement | null>(null)
  const [highlightedId, setHighlightedId] = useState<string | null>(null)
  const label = categoryName ? `${categoryName} workers` : 'workers'
  const titleId = `nearby-title-${jobId}`

  // Candidate pin clicked on the map: scroll its chip into view and highlight it briefly.
  // Keyed on the click token so clicking the same pin twice scrolls again.
  const focusedRef = useRef(focusedCandidate)
  focusedRef.current = focusedCandidate
  const focusToken = focusedCandidate?.token
  useEffect(() => {
    const workerId = focusedRef.current?.workerId
    if (!workerId) return undefined
    const scroller = scrollerRef.current
    const chip = scroller?.querySelector<HTMLElement>(`[data-candidate-chip="${CSS.escape(workerId)}"]`)
    if (!scroller || !chip) return undefined
    // Horizontal only: the page itself never scrolls.
    scroller.scrollTo({
      left: Math.max(0, chip.offsetLeft - (scroller.clientWidth - chip.offsetWidth) / 2),
      behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    })
    chip.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true })
    setHighlightedId(workerId)
    const timer = setTimeout(() => setHighlightedId(null), FOCUS_HIGHLIGHT_MS)
    return () => clearTimeout(timer)
  }, [focusToken])

  let body: ReactNode
  if (!jobLocated) {
    body = (
      <p className="flex h-9 items-center gap-1.5 text-[11px] text-[#718078]">
        <MapPinOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Locate the job on the map to find nearby workers.
      </p>
    )
  } else if (status === 'loading' || status === 'idle') {
    body = (
      <div className="flex gap-2" aria-busy="true" aria-label="Loading nearby workers">
        {Array.from({ length: 3 }).map((_, index) => <Skeleton key={index} className="h-9 w-[224px] shrink-0 rounded-lg" />)}
      </div>
    )
  } else if (status === 'error') {
    body = (
      <p role="alert" className="flex h-9 items-center gap-1.5 text-[11px] text-red-700">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Couldn't load nearby workers ·
        <button type="button" onClick={onRetry} className="inline-flex items-center gap-1 font-semibold underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400">
          <RefreshCw className="h-3 w-3" aria-hidden="true" />
          Retry
        </button>
      </p>
    )
  } else if (displayed.length === 0) {
    body = (
      <p className="flex min-h-9 flex-wrap items-center gap-x-1.5 text-[11px] text-[#718078]">
        <span>No {label} within {radiusKm} km</span>
        {canIncreaseRadius ? (
          <>
            <span aria-hidden="true">·</span>
            <button type="button" onClick={onIncreaseRadius} className="whitespace-nowrap font-semibold text-[#087a49] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#169b62]">
              Increase radius
            </button>
          </>
        ) : null}
      </p>
    )
  } else {
    const hiddenCount = totalCount - candidates.length
    body = (
      <ul
        ref={scrollerRef}
        tabIndex={0}
        aria-labelledby={titleId}
        className="relative flex snap-x gap-2 overflow-x-auto overscroll-x-contain pb-0.5 [scrollbar-width:thin] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#169b62]"
      >
        {displayed.map((candidate) => (
          <CandidateChip
            key={candidate.worker.id}
            candidate={candidate}
            isHovered={hoveredWorkerId === candidate.worker.id}
            isHighlighted={highlightedId === candidate.worker.id}
            onAdd={onAdd}
            onHover={onHover}
          />
        ))}
        {hiddenCount > 0 ? (
          <li className="flex shrink-0 items-center">
            <button
              type="button"
              onClick={onShowMore}
              className="h-9 rounded-lg border border-dashed border-[#9fd4b8] px-3 text-[11px] font-semibold text-[#087a49] hover:bg-[#f3faf6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#169b62]"
            >
              Show {hiddenCount} more
            </button>
          </li>
        ) : null}
      </ul>
    )
  }

  return (
    <section
      aria-labelledby={titleId}
      onClick={(event) => event.stopPropagation()}
      className={cn(
        'rounded-xl bg-white/95 px-2 py-1 shadow-lg backdrop-blur-sm',
        variant === 'overlay' ? 'absolute bottom-7 left-3 right-14 z-20 border border-[#dfeae3]' : 'border border-[#dfeae3]',
      )}
    >
      <header className="flex items-baseline justify-between gap-2">
        <h3 id={titleId} className="truncate text-[11px] font-semibold leading-4 text-[#20352b]">
          Nearby {label}{status === 'ready' && jobLocated ? ` · ${totalCount}` : ''}
        </h3>
        <span className="shrink-0 text-[10px] leading-4 text-[#718078]">nearest first · {radiusKm} km</span>
      </header>
      <div className="mt-0.5">{body}</div>
    </section>
  )
})
